import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Undo2 } from "lucide-react";

import { me } from "../api/auth.ts";
import {
  getLead, updateLead,
  LEAD_STATUS_COLORS, LEAD_STATUS_LABELS, LEAD_STATUSES,
  type Lead, type LeadUpdatePayload,
} from "../api/leads.ts";
import { listJobs } from "../api/jobs.ts";
import { apiFetch } from "../api/client.ts";

function centsToDisplay(cents: number | null): string {
  return cents == null ? "" : (cents / 100).toFixed(2);
}
function displayToCents(val: string): number | null {
  const n = parseFloat(val);
  return isNaN(n) || n < 0 ? null : Math.round(n * 100);
}

type FormState = {
  name: string; phone_e164: string; email: string; address_text: string;
  service_type: string; property_type: string; scheduled_time: string;
  gate_operator_kind: string; job_details: string; service_call_fee: string;
  status: string; urgency: string; notes: string; asap: boolean;
};

function leadToForm(lead: Lead): FormState {
  return {
    name: lead.name ?? "", phone_e164: lead.phone_e164 ?? "",
    email: lead.email ?? "", address_text: lead.address_text ?? "",
    service_type: lead.service_type ?? "", property_type: lead.property_type ?? "",
    scheduled_time: lead.scheduled_time ? new Date(lead.scheduled_time).toISOString().slice(0, 16) : "",
    gate_operator_kind: lead.gate_operator_kind ?? "",
    job_details: lead.job_details ?? "",
    service_call_fee: centsToDisplay(lead.service_call_fee_cents),
    status: lead.status, urgency: lead.urgency ?? "", notes: lead.notes ?? "",
    asap: lead.asap ?? false,
  };
}

function formToPatch(form: FormState, original: Lead): LeadUpdatePayload {
  const patch: LeadUpdatePayload = {};
  const s = (k: keyof LeadUpdatePayload, formVal: string, origVal: string | null | undefined) => {
    if (formVal !== (origVal ?? "")) (patch as Record<string, unknown>)[k] = formVal || undefined;
  };
  s("name", form.name, original.name);
  s("phone_e164", form.phone_e164, original.phone_e164);
  s("email", form.email, original.email);
  s("address_text", form.address_text, original.address_text);
  s("service_type", form.service_type, original.service_type);
  s("gate_operator_kind", form.gate_operator_kind, original.gate_operator_kind);
  s("job_details", form.job_details, original.job_details);
  s("notes", form.notes, original.notes);
  if (form.property_type !== (original.property_type ?? ""))
    patch.property_type = (form.property_type as Lead["property_type"]) || null;
  const origST = original.scheduled_time ? new Date(original.scheduled_time).toISOString().slice(0, 16) : "";
  if (form.scheduled_time !== origST)
    patch.scheduled_time = form.scheduled_time ? new Date(form.scheduled_time).toISOString() : null;
  const feeCents = displayToCents(form.service_call_fee);
  if (feeCents !== original.service_call_fee_cents) patch.service_call_fee_cents = feeCents;
  if (form.status !== original.status) patch.status = form.status as Lead["status"];
  if (form.urgency !== (original.urgency ?? "")) patch.urgency = form.urgency || null;
  if (form.asap !== (original.asap ?? false)) patch.asap = form.asap;
  return patch;
}

// Fields the dispatcher MUST collect before converting to a job
const INTAKE_REQUIRED: (keyof FormState)[] = [
  "name", "address_text", "property_type", "service_type", "gate_operator_kind", "job_details",
];

export default function LeadDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: currentUser } = useQuery({ queryKey: ["auth", "me"], queryFn: me });

  const { data: lead, isLoading } = useQuery({
    queryKey: ["lead", id], queryFn: () => getLead(id), enabled: Boolean(id),
  });
  const { data: linkedJobs } = useQuery({
    queryKey: ["jobs", { lead_id: id }],
    queryFn: () => listJobs({ lead_id: id, limit: 1 }),
    enabled: Boolean(id),
  });
  const linkedJob = linkedJobs?.items[0] ?? null;

  const [form, setForm] = useState<FormState | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => { if (lead && !form) setForm(leadToForm(lead)); }, [lead]);

  const isDirty = lead && form ? Object.keys(formToPatch(form, lead)).length > 0 : false;

  const saveMutation = useMutation({
    mutationFn: (patch: LeadUpdatePayload) => updateLead(id, patch),
    onSuccess: (updated) => { qc.setQueryData(["lead", id], updated); setForm(leadToForm(updated)); setSaveError(null); },
    onError: (e: Error) => setSaveError(e.message),
  });

  const convertMutation = useMutation({
    mutationFn: () => apiFetch<{ id: string }>("/jobs/from-lead", { method: "POST", body: JSON.stringify({ lead_id: id }) }),
    onSuccess: (job) => navigate(`/jobs/${job.id}`),
    onError: (e: Error) => setSaveError(e.message),
  });

  function set<K extends keyof FormState>(field: K, value: FormState[K]) {
    setForm((f) => f ? { ...f, [field]: value } : f);
  }

  const terminalStatuses = ["won", "cancelled", "lost", "duplicate"];
  const canConvert = lead && !terminalStatuses.includes(lead.status) && Boolean(form?.phone_e164 || lead.phone_e164);

  if (isLoading || !lead || !form) {
    return <div className="flex items-center justify-center py-20 text-slate-400">Loading…</div>;
  }

  const missingCount = INTAKE_REQUIRED.filter(k => !form[k]).length;
  const statusColor = LEAD_STATUS_COLORS[form.status as Lead["status"]] ?? "bg-slate-100 text-slate-600";
  const incomplete = (k: keyof FormState) => INTAKE_REQUIRED.includes(k) && !form[k];

  return (
    <div className="mx-auto max-w-3xl space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/leads")} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">{lead.name || "(no name)"}</h1>
            <div className="mt-1 flex items-center gap-2">
              <span className={`badge ${statusColor}`}>
                {LEAD_STATUS_LABELS[form.status as Lead["status"]] ?? form.status}
              </span>
              <span className="text-xs text-slate-400">#{lead.id.slice(0, 8)}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isDirty && (
            <>
              <button className="btn-secondary gap-1.5" onClick={() => { setForm(leadToForm(lead)); setSaveError(null); }} disabled={saveMutation.isPending}>
                <Undo2 className="h-3.5 w-3.5" /> Discard
              </button>
              <button className="btn gap-1.5" onClick={() => saveMutation.mutate(formToPatch(form, lead))} disabled={saveMutation.isPending}>
                <Save className="h-3.5 w-3.5" />
                {saveMutation.isPending ? "Saving…" : "Save changes"}
              </button>
            </>
          )}
          {linkedJob ? (
            <button className="btn-secondary" onClick={() => navigate(`/jobs/${linkedJob.id}`)}>
              View Job →
            </button>
          ) : (
            <button
              className="btn-secondary"
              onClick={() => convertMutation.mutate()}
              disabled={!canConvert || convertMutation.isPending}
            >
              {convertMutation.isPending ? "Converting…" : "Convert to Job →"}
            </button>
          )}
        </div>
      </div>

      {saveError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{saveError}</div>
      )}

      {/* Missing fields banner */}
      {missingCount > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white">{missingCount}</span>
          <p className="text-sm text-red-700">
            {missingCount === 1 ? "1 required field is" : `${missingCount} required fields are`} missing — call the customer back to collect the highlighted information before converting to a job.
          </p>
        </div>
      )}

      {/* ── Contact ── */}
      <div className="card space-y-5">
        <h2 className="section-title">Contact</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name" incomplete={incomplete("name")}>
            <input className="input" value={form.name} onChange={e => set("name", e.target.value)} placeholder="Customer name" />
          </Field>
          <Field label="Phone Number">
            <input className="input" value={form.phone_e164} onChange={e => set("phone_e164", e.target.value)} placeholder="+1 555 000 0000" />
          </Field>
          <Field label="Email" className="sm:col-span-2">
            <input className="input" type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="customer@email.com" />
          </Field>
        </div>
      </div>

      {/* ── Service Location ── */}
      <div className="card space-y-5">
        <h2 className="section-title">Service Location</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Address" className="sm:col-span-2" incomplete={incomplete("address_text")}>
            <input className="input" value={form.address_text} onChange={e => set("address_text", e.target.value)} placeholder="123 Main St, Houston, TX 77001" />
          </Field>
          <Field label="Property Type" incomplete={incomplete("property_type")}>
            <select className="input" value={form.property_type} onChange={e => set("property_type", e.target.value)}>
              <option value="">— Select type —</option>
              <option value="residential">Residential</option>
              <option value="commercial">Commercial</option>
            </select>
          </Field>
        </div>
      </div>

      {/* ── Job Intake ── */}
      <div className="card space-y-5">
        <h2 className="section-title">Job Intake</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Scheduled Time">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => { if (form.asap) { set("asap", false); } else { set("asap", true); set("scheduled_time", ""); } }}
                className={`shrink-0 rounded-md border px-3 py-2 text-xs font-semibold transition-colors ${
                  form.asap
                    ? "border-amber-400 bg-amber-100 text-amber-800"
                    : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                ⚡ ASAP
              </button>
              <input
                className={`input flex-1 ${form.asap ? "opacity-40" : ""}`}
                type="datetime-local"
                disabled={form.asap}
                value={form.asap ? "" : form.scheduled_time}
                onChange={e => { set("asap", false); set("scheduled_time", e.target.value); }}
              />
            </div>
          </Field>
          <Field label="Service Call Fee ($)">
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-slate-400">$</span>
              <input className="input pl-7" type="number" min="0" step="0.01" value={form.service_call_fee} onChange={e => set("service_call_fee", e.target.value)} placeholder="0.00" />
            </div>
          </Field>
          <Field label="Gate & Operator Kind" className="sm:col-span-2" incomplete={incomplete("gate_operator_kind")}>
            <input className="input" value={form.gate_operator_kind} onChange={e => set("gate_operator_kind", e.target.value)} placeholder="e.g. LiftMaster CSL24V on swing gate" />
          </Field>
          <Field label="Job Details" className="sm:col-span-2" incomplete={incomplete("job_details")}>
            <textarea className="input" value={form.job_details} onChange={e => set("job_details", e.target.value)} placeholder="Describe the issue or work needed…" rows={3} />
          </Field>
        </div>
      </div>

      {/* ── Workflow ── */}
      <div className="card space-y-5">
        <h2 className="section-title">Workflow</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Lead Status">
            <select className="input" value={form.status} onChange={e => set("status", e.target.value)}>
              {LEAD_STATUSES.map(s => (
                <option key={s} value={s}>{LEAD_STATUS_LABELS[s]}</option>
              ))}
            </select>
          </Field>
          <Field label="Service Type" incomplete={incomplete("service_type")}>
            <input className="input" value={form.service_type} onChange={e => set("service_type", e.target.value)} placeholder="e.g. Gate repair, Lock re-key" />
          </Field>
          <Field label="Urgency">
            <select className="input" value={form.urgency} onChange={e => set("urgency", e.target.value)}>
              <option value="">— None —</option>
              <option value="emergency">Emergency</option>
              <option value="same_day">Same day</option>
              <option value="scheduled">Scheduled</option>
              <option value="info">Info only</option>
            </select>
          </Field>
        </div>
        <Field label="Internal Note">
          <textarea className="input" value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Notes visible only to your team…" rows={3} />
        </Field>
      </div>

      {/* Raw payload — admin only */}
      {currentUser?.role === "admin" && (
        <details className="card">
          <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-slate-400 hover:text-slate-600">
            Raw API response
          </summary>
          <pre className="mt-4 overflow-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-4 text-xs text-slate-600">{JSON.stringify(lead, null, 2)}</pre>
        </details>
      )}
    </div>
  );
}

function Field({ label, children, className = "", incomplete = false }: {
  label: string; children: React.ReactNode; className?: string; incomplete?: boolean;
}) {
  return (
    <div className={`field ${incomplete ? "field-incomplete" : ""} ${className}`}>
      <label className="field-label">
        {label}
        {incomplete && <span className="ml-1.5 text-[10px] font-semibold normal-case tracking-normal text-red-500">· missing</span>}
      </label>
      {children}
    </div>
  );
}
